import { useState, useMemo, useCallback } from 'react';
import { useGo, useCustomMutation, useApiUrl } from '@refinedev/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, UserCheck, RefreshCw, Eye, Download } from 'lucide-react';
import { notify } from '@/lib/notify';
import { Badge } from '@/components/ui/badge';
import { DataTable, type Column, type SortState, type BulkAction } from '@/components/tables';
import { useConfirm, ConfirmDialog } from '@/hooks/use-confirm';
import { getTargetTypeLabel, type Order, type AdminUser } from './types';
import type { OrdersFilterValues } from './OrdersFiltersToolbar';

interface OrdersTableViewProps {
  filterValues: OrdersFilterValues;
  admins: AdminUser[];
  statuses: Array<{ id: number; name: string; color: string }>;
}

interface OrdersListResponse {
  data: Order[];
  total: number;
  page: number;
  limit: number;
}

const currencyFormatter = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

function formatCurrency(value: string | number, currency?: string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  const formatted = currencyFormatter.format(num);
  return currency ? `${formatted} ${currency}` : formatted;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function OrdersTableView({ filterValues, admins, statuses }: OrdersTableViewProps) {
  const go = useGo();
  const apiUrl = useApiUrl();
  const queryClient = useQueryClient();
  const { confirm, isOpen, options, handleConfirm, handleCancel, handleOpenChange } = useConfirm();

  const [page, setPage] = useState(1);
  const [pageSize, _setPageSize] = useState(20);
  const [sort, setSort] = useState<SortState>({ field: 'id', order: 'desc' });

  const adminMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const admin of admins) {
      map.set(admin.id, admin.username);
    }
    return map;
  }, [admins]);

  const statusMap = useMemo(() => {
    const map = new Map<number, { name: string; color: string }>();
    for (const s of statuses) {
      map.set(s.id, { name: s.name, color: s.color });
    }
    return map;
  }, [statuses]);

  // Build query params for GET /orders
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(pageSize));
    params.set('sortBy', sort.field);
    params.set('sortOrder', sort.order);
    if (filterValues.search) { params.set('search', filterValues.search); }
    if (filterValues.assignedAdminId !== undefined) { params.set('assignedAdminId', String(filterValues.assignedAdminId)); }
    if (filterValues.targetType) { params.set('targetType', filterValues.targetType); }
    if (filterValues.targetId !== undefined) { params.set('targetId', String(filterValues.targetId)); }
    if (filterValues.pricingTierId !== undefined) { params.set('pricingTierId', String(filterValues.pricingTierId)); }
    if (filterValues.totalFrom !== undefined) { params.set('totalFrom', String(filterValues.totalFrom)); }
    if (filterValues.totalTo !== undefined) { params.set('totalTo', String(filterValues.totalTo)); }
    if (filterValues.createdFrom) { params.set('createdFrom', filterValues.createdFrom); }
    if (filterValues.createdTo) { params.set('createdTo', filterValues.createdTo); }
    if (filterValues.currency) { params.set('currency', filterValues.currency); }
    return params;
  }, [page, pageSize, sort, filterValues]);

  const { data, isLoading, refetch } = useQuery<OrdersListResponse>({
    queryKey: ['orders-table', queryParams.toString()],
    queryFn: async () => {
      const response = await fetch(`${apiUrl}/orders?${queryParams.toString()}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Ошибка загрузки заказов');
      }
      const body = await response.json() as { success: boolean; data: Order[]; meta: { page: number; perPage: number; total: number; totalPages: number } };
      return {
        data: body.data,
        total: body.meta.total,
        page: body.meta.page,
        limit: body.meta.perPage,
      };
    },
    staleTime: 30_000,
  });

  const deleteMutation = useCustomMutation();
  const assignMutation = useCustomMutation();
  const changeStatusMutation = useCustomMutation();

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['orders-table'] });
    queryClient.invalidateQueries({ queryKey: ['kanban-column'] });
    void refetch();
  }, [queryClient, refetch]);

  const handleBulkDelete = useCallback(async (selected: Order[]) => {
    const confirmed = await confirm({
      title: `Удалить ${selected.length} заказов?`,
      description: 'Заказы будут мягко удалены. Это действие можно будет отменить.',
      confirmText: 'Удалить',
      variant: 'destructive',
    });
    if (!confirmed) { return; }
    deleteMutation.mutate(
      {
        url: '/orders/bulk/delete',
        method: 'post',
        values: { ids: selected.map((o) => o.id) },
      },
      { onSuccess: invalidate },
    );
  }, [confirm, deleteMutation, invalidate]);

  const handleBulkAssignAdmin = useCallback(async (selected: Order[]) => {
    const confirmed = await confirm({
      title: `Снять назначение с ${selected.length} заказов?`,
      description: 'Администратор будет снят со всех выбранных заказов.',
      confirmText: 'Снять',
    });
    if (!confirmed) { return; }
    assignMutation.mutate(
      {
        url: '/orders/bulk/assign',
        method: 'post',
        values: { ids: selected.map((o) => o.id), assignedAdminId: null },
      },
      { onSuccess: invalidate },
    );
  }, [confirm, assignMutation, invalidate]);

  const handleBulkChangeStatus = useCallback(async (selected: Order[], statusId: number) => {
    const status = statusMap.get(statusId);
    const confirmed = await confirm({
      title: `Изменить статус ${selected.length} заказов?`,
      description: `Новый статус: «${status?.name ?? statusId}»`,
      confirmText: 'Применить',
    });
    if (!confirmed) { return; }
    await Promise.allSettled(
      selected.map((order) =>
        new Promise<void>((resolve) => {
          changeStatusMutation.mutate(
            {
              url: `/orders/${order.id}/crm-status`,
              method: 'patch',
              values: { statusId },
            },
            { onSuccess: () => resolve(), onError: () => resolve() },
          );
        }),
      ),
    );
    invalidate();
  }, [confirm, changeStatusMutation, statusMap, invalidate]);

  const columns: Column<Order>[] = useMemo(() => [
    {
      key: 'id',
      header: 'ID',
      sortable: true,
      width: '70px',
      render: (_, record) => (
        <span className="font-mono text-xs text-muted-foreground">#{record.id}</span>
      ),
    },
    {
      key: 'user',
      header: 'Пользователь',
      render: (_, record) => {
        const name = [record.user?.firstName, record.user?.lastName].filter(Boolean).join(' ')
          || record.user?.username
          || `#${record.userId}`;
        return <span className="font-medium">{name}</span>;
      },
    },
    {
      key: 'statusConfigId',
      header: 'Статус',
      sortable: true,
      render: (_, record) => {
        const s = statusMap.get(record.statusConfigId);
        if (!s) { return <span className="text-muted-foreground">—</span>; }
        return (
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
            <span className="text-sm">{s.name}</span>
          </div>
        );
      },
    },
    {
      key: 'total',
      header: 'Сумма',
      sortable: true,
      render: (_, record) => (
        <span className="font-mono font-semibold">{formatCurrency(record.total, record.currency)}</span>
      ),
    },
    {
      key: 'items',
      header: 'Товары',
      render: (_, record) => {
        const items = record.items ?? [];
        if (items.length === 0) { return <span className="text-muted-foreground">—</span>; }
        const first = items[0];
        const label = `${getTargetTypeLabel(first.targetType)}: ${first.target?.name ?? `#${first.targetId}`}`;
        return (
          <div className="flex items-center gap-1">
            <span className="text-sm truncate max-w-[160px]">{label}</span>
            {items.length > 1 && (
              <Badge variant="secondary" className="text-[10px] px-1 py-0">+{items.length - 1}</Badge>
            )}
          </div>
        );
      },
    },
    {
      key: 'assignedAdminId',
      header: 'Менеджер',
      hiddenOnMobile: true,
      render: (_, record) => {
        if (!record.assignedAdminId) { return <span className="text-muted-foreground">—</span>; }
        const name = adminMap.get(record.assignedAdminId);
        return <span className="text-sm">{name ?? `#${record.assignedAdminId}`}</span>;
      },
    },
    {
      key: 'createdAt',
      header: 'Создан',
      sortable: true,
      hiddenOnMobile: true,
      render: (_, record) => (
        <span className="text-sm text-muted-foreground">{formatDate(record.createdAt)}</span>
      ),
    },
  ], [statusMap, adminMap]);

  const bulkActions: BulkAction<Order>[] = useMemo(() => [
    {
      label: 'Просмотр',
      icon: <Eye className="h-3.5 w-3.5" />,
      variant: 'outline' as const,
      showOnlyWhenSelected: true,
      onClick: (selected: Order[]) => {
        if (selected.length === 1) {
          go({ to: `/orders/show/${selected[0].id}` });
        }
      },
    },
    {
      label: 'Экспорт',
      icon: <Download className="h-3.5 w-3.5" />,
      variant: 'outline' as const,
      showOnlyWhenSelected: true,
      onClick: (_selected: Order[]) => {
        notify.info('Экспорт заказов пока не реализован');
      },
    },
    ...statuses.slice(0, 5).map((s) => ({
      label: `→ ${s.name}`,
      icon: <RefreshCw className="h-3.5 w-3.5" />,
      variant: 'outline' as const,
      showOnlyWhenSelected: true,
      onClick: (selected: Order[]) => { void handleBulkChangeStatus(selected, s.id); },
    })),
    {
      label: 'Снять менеджера',
      icon: <UserCheck className="h-3.5 w-3.5" />,
      variant: 'outline',
      showOnlyWhenSelected: true,
      onClick: (selected: Order[]) => { void handleBulkAssignAdmin(selected); },
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-3.5 w-3.5" />,
      variant: 'destructive',
      showOnlyWhenSelected: true,
      onClick: (selected: Order[]) => { void handleBulkDelete(selected); },
    },
  ], [go, statuses, handleBulkChangeStatus, handleBulkAssignAdmin, handleBulkDelete]);

  const orders = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <DataTable<Order>
        columns={columns}
        data={orders}
        isLoading={isLoading}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        sort={sort}
        onSortChange={(s) => { setSort(s ?? { field: 'id', order: 'desc' }); setPage(1); }}
        bulkActions={bulkActions}
        selectable
        onRowClick={(order) => go({ to: `/orders/show/${order.id}` })}
        emptyMessage="Нет заказов по выбранным фильтрам"
      />
      <ConfirmDialog
        isOpen={isOpen}
        options={options}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        onOpenChange={handleOpenChange}
      />
    </>
  );
}
